import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create a new facility under a tenant
 */
export const createFacility = async (tenantId, facilityData, createdBy) => {
  if (!tenantId) throw new Error('Tenant ID is required');

  if (!facilityData.shortName || !facilityData.fullName) {
    throw new Error('Short Name and Full Name are required');
  }

  const facilitiesRef = collection(db, 'tenantmaster', tenantId, 'facilities');

  // Check shortName uniqueness
  const q = query(
    facilitiesRef,
    where('shortName', '==', facilityData.shortName.toUpperCase())
  );

  const existingSnapshot = await getDocs(q);
  if (!existingSnapshot.empty) {
    throw new Error('Short Name already exists for this tenant');
  }

  const docRef = await addDoc(facilitiesRef, {
    shortName: facilityData.shortName.toUpperCase(),
    fullName: facilityData.fullName || '',

    // Backward compatibility
    facilityName: facilityData.fullName || '',

    address: facilityData.address || '',

    mapCoordinates: {
      lat: facilityData.mapCoordinates?.lat || '',
      lng: facilityData.mapCoordinates?.lng || ''
    },

    contact: {
      name: facilityData.contact?.name || '',
      email: facilityData.contact?.email || '',
      mobile: facilityData.contact?.mobile || ''
    },

    tenantId,
    facilityType: facilityData.facilityType || 200,
    status: facilityData.status || 'active',

    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return docRef.id;
};

/**
 * Fetch all facilities for a tenant
 */
export const getFacilitiesByTenant = async (tenantId) => {
  if (!tenantId) return [];

  const facilitiesRef = collection(db, 'tenantmaster', tenantId, 'facilities');
  const snapshot = await getDocs(facilitiesRef);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const deleteFacility = async (tenantId, facilityId) => {
  if (!tenantId || !facilityId) {
    throw new Error('Tenant ID and Facility ID are required');
  }

  try {
    // 1. Delete all facility admins
    const usersRef = collection(db, 'users');

    const q = query(
      usersRef,
      where('facilityId', '==', facilityId),
      where('role', '==', 'facilityadmin')
    );

    const snapshot = await getDocs(q);

    for (const docSnap of snapshot.docs) {
      await deleteDoc(doc(db, 'users', docSnap.id));
    }

    // 2. Delete facility document
    const facilityRef = doc(
      db,
      'tenantmaster',
      tenantId,
      'facilities',
      facilityId
    );

    await deleteDoc(facilityRef);

  } catch (error) {
    console.error('Error deleting facility:', error);
    throw error;
  }
};

/**
 * Update a facility
 */
export const updateFacility = async (tenantId, facilityId, updatedData) => {
  if (!tenantId || !facilityId) {
    throw new Error('Tenant ID and Facility ID are required');
  }

  const facilityRef = doc(
    db,
    'tenantmaster',
    tenantId,
    'facilities',
    facilityId
  );
  if (updatedData.shortName) {
    delete updatedData.shortName;
  }
  await updateDoc(facilityRef, {
    ...updatedData,
    updatedAt: serverTimestamp()
  });
};
